import { ReactNode, useEffect, useState } from "react"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SearchSelect from "Components/molecule/inputs/search-select"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import useEnterSearch from "hooks/useEnterSearch"
import { CrossFilter } from "hooks/useCrossFilter"

export interface HrFilterDropdown {
    key: string
    label: string
    // One value at a time (e.g. Month) instead of a multi-select
    single?: boolean
    // Fixed option list, in this order (e.g. the months). Default: the values in the data, A-Z, with counts.
    options?: string[]
}

interface FilterBarProps {
    filter: CrossFilter
    dropdowns: HrFilterDropdown[]
    // Extra fields before the dropdowns (fiscal year...); the page applies them in onSearch
    children?: ReactNode
    onSearch?: () => void
    onClear?: () => void
}

type Draft = Record<string, string[]>

// Standard filter bar for the HR reports: fields on row 1, Search / Clear Search on row 2.
// Dropdown changes apply on Search (or Enter). A chart click applies right away and shows up here.
const FilterBar = ({ filter, dropdowns, children, onSearch, onClear }: FilterBarProps) => {
    const applied: Draft = Object.fromEntries(dropdowns.map(d => [d.key, filter.selectedFor(d.key)]))
    const appliedKey = JSON.stringify(applied)
    const [draft, setDraft] = useState<Draft>(applied)

    // Keep the fields in step with what is applied (chart clicks, stat cards, Clear Search)
    useEffect(() => { setDraft(applied) }, [appliedKey])

    const setValues = (key: string, values: string[]) => setDraft(d => ({ ...d, [key]: values }))

    const handleSearch = () => {
        dropdowns.forEach(d => filter.select(d.key, draft[d.key] ?? []))
        onSearch?.()
    }

    const handleClear = () => {
        filter.clear()
        onClear?.()
    }

    const { selectProps } = useEnterSearch(handleSearch)

    return (
        <div className="section-container">
            <SearchFormContainer
                inputs={<>
                    {children}
                    {dropdowns.map(d => {
                        const options = d.options
                            ? d.options.map(value => ({ value, label: value }))
                            : filter.countsFor(d.key)
                                .sort((a, b) => a.value.localeCompare(b.value))
                                .map(c => ({ value: c.value, label: `${c.value} (${c.count})` }))
                        const selected = draft[d.key] ?? []

                        return d.single
                            ? <SearchSelect
                                key={d.key}
                                label={d.label}
                                placeholder="All"
                                options={[{ value: "", label: "All" }, ...options]}
                                value={selected[0] ?? ""}
                                onChange={(o) => setValues(d.key, o?.value ? [o.value] : [])}
                                {...selectProps}
                            />
                            : <MultiSelect
                                key={d.key}
                                label={d.label}
                                placeholder="All"
                                options={options}
                                value={options.filter(o => selected.includes(o.value))}
                                onChange={(values) => setValues(d.key, values.map(v => v.value))}
                                {...selectProps}
                            />
                    })}
                </>}
                clearSearchButton={<>
                    <PrimaryButton onClick={handleSearch}>Search</PrimaryButton>
                    <SecondaryButton onClick={handleClear}>Clear Search</SecondaryButton>
                </>}
            />
        </div>
    )
}

export default FilterBar
